import { useCallback, useEffect, useRef, useState } from "react";
import { RootState } from "typedefs";
import { shallowEqual, useSelector } from "react-redux";
import { fetchSpaceMemberships } from "app/actions/space";
import { useActions } from "app/utils/hooks";
import { DemoSpaceModel, fetchDemoSpaces } from "app/actions/DemoSpace";
import useDebounce from "app/utils/hooks/useDebounce";
import SpaceModel from "app/models/SpaceModel";
import { useHistory } from "react-router-dom";
import useQuery from "app/utils/hooks/useQuery";
import { usePagination } from "app/utils/hooks/usePagination";
import SmartListModel from "app/models/SmartListModel";

type queryType = {
  page: number;
  forcePerPage: number;
  perPage: number;
  options: {
    include: string[];
    exclude: string[];
  };
  athlete_name?: string;
  segment_id?: string;
  smart_list_id?: string;
};

const DEFAULT_LIST_VALUE = {
  Object: "athlete_space",
  name: "All Spaces",
  id: "1",
};
const FORCE_PER_PAGE = 100;
const PER_PAGE = FORCE_PER_PAGE;
const INIT_PAGE = 1;

const useSpacesModel = () => {
  const abortController = useRef<AbortController | null>(null);

  const spaceActions = useActions({
    fetchDemoSpaces,
    fetchSpaceMemberships,
  });

  const { self } = useSelector((state: RootState) => state.user);
  // Inspiration loads through fetchDemoSpace below, which neither
  // usePagination nor the demoSpace store tracks, so it keeps its own flag.
  const [demoPending, setDemoPending] = useState(false);

  const searchParams = useQuery();

  const history = useHistory();

  const route = (path: string) => {
    history.push(path);
  };

  const [showUpgrade, setUpgradeState] = useState(false);
  const [selectedListValue, setSelectedListValue] =
    useState<SmartListModel>(DEFAULT_LIST_VALUE);
  const [searchTerm, setSearch] = useState<string>("");
  const { isModalOpen } = useSelector(
    (state: RootState) => state.space,
    shallowEqual,
  );

  const newSingle = () => {
    if (!self?.planPermissions?.includes("create_spaces")) {
      return upgradeHandler();
    }

    return history.push("/spaces/new");
  };

  const transformQueryName = useCallback((name: string) => {
    const queryName = searchParams.get("name");
    return (
      name.replace(/_/g, " ").charAt(0).toUpperCase() +
      (queryName ?? "").replace(/_/g, " ").slice(1)
    );
  }, []);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const INIT_QUERY: queryType = {
    page: INIT_PAGE,
    forcePerPage: PER_PAGE,
    perPage: PER_PAGE,
    options: {
      include: ["team_info"],
      exclude: ["permissions", "state"],
    },
    athlete_name: debouncedSearch,
  };

  const [spaceQuery, setSpaceQuery] = useState<queryType>(INIT_QUERY);

  // const openUpgrade = () => {
  //   setUpgradeState(true);
  // };

  const closeUpgrade = () => {
    setUpgradeState(false);
  };

  const {
    data,
    pending,
    sentryRef,
    updateData,
    loadInitialData,
    clearData,
    cancel: cancelSpacesFetch,
  }: any = usePagination({
    initialParams: { ...spaceQuery, athlete_name: debouncedSearch },
    fetchData: (params, signal) =>
      spaceActions.fetchSpaceMemberships(params, signal),
    clearDataAction: () => {},
    dependencies: [spaceQuery, debouncedSearch],
    fetchOnMount: false,
  });

  const isDemo = searchParams.get("object") === "demo_space";

  useEffect(() => {
    const queryObject = searchParams.get("object");
    const queryName = searchParams.get("name");
    const queryId = searchParams.get("id");

    if (queryObject || queryName) {
      if (queryObject !== "demo_space") {
        const newObject = {
          name: transformQueryName(queryName),
          object: queryObject,
          id: queryId,
        };
        setSelectedListValue(newObject);
        smartListSelectHandler(newObject);
      } else {
        setSelectedListValue(null);
        fetchDemoSpace();
      }
    } else {
      // if there is no query detected in URL need to make data fetch
      loadInitialData(INIT_QUERY);
    }
  }, []);

  const fetchDemoSpace = async () => {
    clearData();
    if (abortController.current) {
      abortController.current.abort();
    }

    const controller = new AbortController();
    abortController.current = controller;
    setDemoPending(true);
    try {
      const res: any = await fetchDemoSpaces(null, null, controller.signal);

      // Switching to a non-demo filter aborts this controller; a response
      // that slipped through anyway is stale and must not overwrite the new
      // filter's data.
      if (controller.signal.aborted) {
        return;
      }

      if (res && res._hash) {
        const demoSpaces = Object.values(res._hash).reduce(
          (acc: DemoSpaceModel[], item: DemoSpaceModel) => {
            if (item.space) {
              acc.push(item.space);
            }
            return acc;
          },
          [],
        );

        updateData(() => {
          return [...(demoSpaces as SpaceModel[])];
        });
      }
    } catch (error) {
      // The request util rejects aborted calls with a 499 "Request Canceled"
      // object rather than a DOM AbortError; both just mean a newer filter
      // superseded this request.
      if (error?.name === "AbortError" || error?.status === 499) {
        console.log("Request was aborted.");
      } else {
        console.error(error);
      }
    } finally {
      // A newer Inspiration request owns the flag now; leave it on for that.
      if (abortController.current === controller) {
        setDemoPending(false);
      }
    }
  };

  const spaceClicked = (value: string) => {
    if (value.includes("demo")) {
      route(value);
    } else {
      route(`/spaces/${value}`);
    }
  };

  const updateSearch = useCallback(
    (value: string) => {
      if (value != null && value.length > 0) {
        setSearch(value);
      } else {
        setSearch("");
      }
    },
    [setSearch],
  );

  const smartListSelectHandler = useCallback(
    (value: SmartListModel) => {
      if (value?.object !== "demo_space") {
        // Abandon an in-flight Inspiration fetch: its late response would
        // otherwise overwrite the newly selected filter's data.
        if (abortController.current) {
          abortController.current.abort();
        }
        setDemoPending(false);

        setSelectedListValue(value);

        if (debouncedSearch?.length >= 3 || debouncedSearch?.length === 0) {
          if (value?.object === "smart_list") {
            return setSpaceQuery({ ...INIT_QUERY, smart_list_id: value?.id });
          } else if (value?.object === "segment") {
            return setSpaceQuery({ ...INIT_QUERY, segment_id: value?.id });
          }
        }
      }

      if (value?.object === "athlete_space") {
        setSelectedListValue(DEFAULT_LIST_VALUE);
        // Spread: the memoized handler closes over the same INIT_QUERY object
        // that seeded the spaceQuery state, and setting an identical reference
        // bails out of the update — usePagination's deps never change and the
        // memberships fetch silently never fires (e.g. Inspiration → All
        // Spaces). A fresh object guarantees the refetch.
        setSpaceQuery({ ...INIT_QUERY });
        return;
      }

      if (value?.object === "demo_space") {
        // The mirror race: a memberships request still in flight would get
        // appended onto the demo list when it lands.
        cancelSpacesFetch();
        setSelectedListValue(null);
        fetchDemoSpace();
        return;
      }
    },
    [setSelectedListValue, cancelSpacesFetch],
  );

  const upgradeHandler = () => {
    setUpgradeState((prev) => !prev);
  };

  return {
    spacesData: data,
    pending,
    sentryRef,
    self,
    history,
    debouncedSearch,
    isModalOpen,
    showUpgrade,
    closeUpgrade,
    spaceClicked,
    updateSearch,
    selectedListValue,
    smartListSelectHandler,
    upgradeHandler,
    newSingle,
    isDemo,
    demoPending,
  };
};

export default useSpacesModel;
